import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AudioCaptureWaveForm } from "./AudioCaptureWaveForm";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { MdsIcon } from "@/design-system/components/icon";
import { forwardRef, Ref } from "react";

interface AudioCaptureIndicatorProps {
  expanded?: boolean;
  insideNote?: boolean;
  onClickElse?: () => void;
  rightIconKind?: MdsIconKind;
}

export const AudioCaptureIndicator = observer(
  forwardRef<HTMLButtonElement, AudioCaptureIndicatorProps>(function AudioCaptureIndicator(
    {
      expanded = false,
      insideNote = false,
      onClickElse,
      rightIconKind,
    }: AudioCaptureIndicatorProps,
    ref: Ref<HTMLButtonElement>
  ) {
    const { store } = useAppStore();
    const { activeNoteId, formattedDuration, isIndicatorVisible } = store.audioCapture;

    const showText = expanded || insideNote;

    if (!isIndicatorVisible) return null;
    return (
      <StyledButton
        ref={ref}
        variant={MDSButtonVariant.EmphasisLight}
        $insideNote={insideNote}
        $expanded={expanded}
        onClick={() => {
          if (!activeNoteId) {
            onClickElse?.();
            return;
          }
          if (store.routing.noteIdParam === activeNoteId) {
            onClickElse?.();
          }
          store.navigation.goToNote({ noteId: activeNoteId });
        }}
      >
        <AudioCaptureWaveForm />
        {showText && (
          <MDSText
            variant={MDSTextVariant.TextS}
            color={MDSTextColor.Emphasis}
            weight={MDSTextWeight.Medium}
          >
            {formattedDuration}
          </MDSText>
        )}
        {rightIconKind && (
          <StyledRightIcon kind={rightIconKind} slot={MDSButtonSlotName.RightIcon} />
        )}
      </StyledButton>
    );
  })
);

const StyledButton = styled(MDSButton)<{ $expanded: boolean; $insideNote: boolean }>`
  height: ${({ $expanded, $insideNote }) => ($insideNote ? "32px" : $expanded ? "auto" : "24px")};
  width: ${({ $expanded, $insideNote }) => ($insideNote ? "120px" : $expanded ? "auto" : "24px")};
`;

const StyledRightIcon = styled(MdsIcon)`
  border-radius: ${({ theme }) => theme.borderRadius.max};
`;
